import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import EducationalContentManager from './EducationalContentManager';

export default function TaskTemplateEditor({ templates = [], onChange, className = '' }) {
  const [local, setLocal] = useState(templates);
  const [expandedContentId, setExpandedContentId] = useState(null);
  const lastSyncedTemplatesRef = useRef(templates);

  // Sync local state when templates prop changes (e.g., when data loads from GraphQL)
  useEffect(() => {
    // Always sync if templates prop has changed
    // Compare by creating a stable representation
    const lastSynced = lastSyncedTemplatesRef.current || [];
    const lastSyncedStr = JSON.stringify(lastSynced.map(t => ({ id: t.id, title: t.title })));
    const propStr = JSON.stringify(templates.map(t => ({ id: t.id, title: t.title })));
    
    // Sync if templates prop has changed (different content or length)
    // This handles initial load and updates from server
    if (lastSyncedStr !== propStr || lastSynced.length !== templates.length) {
      console.log(`TaskTemplateEditor: Syncing ${templates.length} templates from prop`);
      setLocal(templates);
      lastSyncedTemplatesRef.current = templates;
    }
  }, [templates]);

  const commit = (next) => {
    setLocal(next);
    lastSyncedTemplatesRef.current = next;
    if (onChange) onChange(next);
  };

  const add = () => {
    const next = [
      ...local,
      {
        id: `tpl-${Date.now()}`,
        title: 'New Task',
        description_md: '',
        estimated_minutes: 60,
        requires_attachment: false,
        day_offset: local.length,
        canonical_task_type: 'custom',
        educationalContent: [],
      },
    ];
    commit(next);
  };

  const removeAt = (idx) => {
    const next = local.filter((_, i) => i !== idx).map((t, i) => ({ ...t, day_offset: i }));
    commit(next);
  };

  const updateAt = (idx, patch) => {
    const next = local.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    commit(next);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Task Templates</h3>
          <p className="text-sm text-muted-foreground">Day offset is calculated automatically.</p>
        </div>
        <button type="button" onClick={add} className="rounded-lg border border-border bg-card px-3 py-2 hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">
          Add Task
        </button>
      </div>
      {local.length === 0 ? (
        <div className="p-8 text-center border rounded-lg border-dashed text-muted-foreground">
          No tasks yet. Add your first task template.
        </div>
      ) : (
        local.map((t, idx) => (
          <div key={t.id || idx} className="p-4 rounded-lg border bg-card space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs rounded px-2 py-1 bg-muted">Day {t.day_offset}</span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="text-destructive hover:bg-destructive/10 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
              >
                Remove
              </button>
            </div>
            <input
              value={t.title}
              onChange={(e) => updateAt(idx, { title: e.target.value })}
              placeholder="Task title"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
            />
            <textarea
              value={t.description_md || ''}
              onChange={(e) => updateAt(idx, { description_md: e.target.value })}
              placeholder="Task description (markdown supported)"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Task Type</label>
                <select
                  value={t.canonical_task_type || 'custom'}
                  onChange={(e) => updateAt(idx, { canonical_task_type: e.target.value })}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                >
                  {['valuation_prep','document_review','financial_analysis','legal_compliance','continuity_planning','team_assessment','custom'].map((v) => (
                    <option key={v} value={v}>{v.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Estimated Time (min)</label>
                <input
                  type="number"
                  min={0}
                  value={t.estimated_minutes || 0}
                  onChange={(e) => updateAt(idx, { estimated_minutes: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!t.requires_attachment}
                    onChange={(e) => updateAt(idx, { requires_attachment: e.target.checked })}
                    className="focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  />
                  Requires Attachment
                </label>
              </div>
            </div>

            {/* Educational Content Section */}
            <div className="border-t border-border pt-3 mt-3">
              <button
                type="button"
                onClick={() => setExpandedContentId(expandedContentId === t.id ? null : t.id)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
              >
                {expandedContentId === t.id ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
                Educational Content
                {t.educationalContent && t.educationalContent.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                    {t.educationalContent.length}
                  </span>
                )}
              </button>

              {expandedContentId === t.id && (
                <div className="mt-3">
                  <EducationalContentManager
                    content={t.educationalContent || []}
                    onChange={(content) => updateAt(idx, { educationalContent: content })}
                  />
                </div>
              )}
            </div>
          </div>
        ))
      )}
      {local.length > 0 && (
        <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
          <div className="text-sm">
            <span className="font-medium">{local.length}</span> task{local.length !== 1 ? 's' : ''}
          </div>
          <div className="text-sm text-muted-foreground">
            Program duration: <span className="font-medium">{local[local.length - 1].day_offset + 1}</span> days
          </div>
        </div>
      )}
    </div>
  );
}


